import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import {useTranslation} from "react-i18next";
import type {DateRange} from "../../common/newDatePicker/datePickerShared";
import {useMemo} from "react";
import {useInsightTargets} from "./targets/useInsightTargets";
import {activeTargetPeriod, resolveChartTargets} from "./targets/targetResolution";
import {TARGET_METRICS_BY_ID} from "./targets/targetDefinitions";
import ChannelsTrendCard from "./crm-overview/ChannelsTrendCard";
import CrmTrendCard from "./crm-overview/CrmTrendCard";
import OverviewStatCards from "./crm-overview/OverviewStatCards";
import {insightsLoadingOf} from "../kit/insightsLoading";
import {CRM_TREND_METRICS, type CrmTrendMetricKey} from "./crm-overview/crmTrendMetrics";
import {OVERVIEW_STAT_METRICS} from "./crm-overview/overviewStatMetrics";
import RatesTrendCard from "./crm-overview/RatesTrendCard";
import SeasonalityCard from "./crm-overview/SeasonalityCard";
import {
    useInsightsV2ChannelTimeSeries,
    useInsightsV2OverviewStats,
    useInsightsV2RateTimeSeries,
    useInsightsV2Seasonality,
    useInsightsV2TimeSeries,
    buildRequest,
} from "./useInsightsV2Stats";

/** One order for the grid and for the target lookup, so a card can never be judged against another's budget. */
const CRM_TREND_ORDER = Object.keys(CRM_TREND_METRICS) as CrmTrendMetricKey[];

interface CrmOverviewTabProps {
    range: DateRange;
    compareRange: DateRange | null;
    /** The rail's search. A section whose every card is filtered out drops its heading with them. */
    matches?: (title: string, key: string) => boolean;
}

/**
 * Commercial performance: rate, revenue and stay metrics, all scored against their targets. The range and the
 * targets dialog belong to the surrounding layout, so this owns only the section's body.
 */
export function CrmOverviewTab({range, compareRange, matches}: CrmOverviewTabProps) {
    const {t} = useTranslation();
    const targetsQuery = useInsightTargets();
    const statsQuery = useInsightsV2OverviewStats(range, compareRange);
    const timeSeriesQuery = useInsightsV2TimeSeries(range, compareRange);
    const rateTimeSeriesQuery = useInsightsV2RateTimeSeries(range);
    const channelTimeSeriesQuery = useInsightsV2ChannelTimeSeries(range);
    const seasonalityQuery = useInsightsV2Seasonality();
    const timeSeries = timeSeriesQuery.data;
    const targetPeriod = activeTargetPeriod(targetsQuery.data);
    const targetRange = useMemo(() => buildRequest(range), [range]);
    const statsLoading = insightsLoadingOf(
        statsQuery.isLoading,
        compareRange != null && statsQuery.isPreviousData,
    );
    const timeSeriesLoading = insightsLoadingOf(
        timeSeriesQuery.isLoading,
        compareRange != null && timeSeriesQuery.isPreviousData,
    );
    const trendTargets = useMemo(() => {
        const points = timeSeries?.points ?? [];
        const resolved = (id: string) => {
            const definition = TARGET_METRICS_BY_ID.get(id);
            return definition
                ? resolveChartTargets(targetPeriod, definition, targetRange, points)
                : undefined;
        };

        return Object.fromEntries(
            CRM_TREND_ORDER.map(metric => [metric, resolved(metric)]),
        ) as Record<CrmTrendMetricKey, Array<number | null> | undefined>;
    }, [targetPeriod, targetRange, timeSeries?.points]);

    const shows = (title: string, key: string = title) => matches == null || matches(t(title), key);
    const visibleTrends = CRM_TREND_ORDER.filter(metric => shows(CRM_TREND_METRICS[metric].title, metric));
    const showStats = OVERVIEW_STAT_METRICS.some(definition => shows(definition.title, definition.id));
    const showRates = shows("Rates over time");
    const showChannels = shows("Channels over time");
    const showSeasonality = shows("Seasonality");
    const showTrends = visibleTrends.length > 0 || showRates || showChannels;

    if (!showStats && !showTrends && !showSeasonality) {
        return (
            <Typography variant={"body2"} color={"text.secondary"} sx={{py: 6, textAlign: "center"}}>
                {t("No insights match your search.")}
            </Typography>
        );
    }

    return (
        <Stack gap={3}>
            {showStats && (
            <Stack gap={1}>
                <Typography variant="body1" color="text.secondary">
                    {t("All metrics")}
                </Typography>

                <OverviewStatCards
                    tiles={statsQuery.data}
                    loading={statsLoading}
                    targetLoading={insightsLoadingOf(targetsQuery.isLoading)}
                    targetPeriod={targetPeriod}
                    targetRange={targetRange}
                    comparing={compareRange != null}
                    matches={matches}
                />
            </Stack>
            )}

            {showTrends && (
            <Stack gap={1}>
                <Typography variant="body1" color="text.secondary">
                    {t("Trends")}
                </Typography>

                <Box
                    sx={{
                        display: "grid",
                        gridTemplateColumns: {xs: "minmax(0, 1fr)", xl: "repeat(2, minmax(0, 1fr))"},
                        gap: 2,
                        minWidth: 0,
                    }}
                >
                    {visibleTrends.map(metric => (
                        <CrmTrendCard
                            key={metric}
                            metric={metric}
                            data={timeSeries}
                            tile={statsQuery.data?.find(tile => tile.key === metric)}
                            range={range}
                            targets={trendTargets[metric]}
                            loading={timeSeriesLoading}
                        />
                    ))}
                    {showRates && <RatesTrendCard
                        data={rateTimeSeriesQuery.data}
                        loading={insightsLoadingOf(rateTimeSeriesQuery.isLoading, rateTimeSeriesQuery.isPreviousData)}
                    />}
                    {showChannels && <ChannelsTrendCard
                        data={channelTimeSeriesQuery.data}
                        loading={insightsLoadingOf(channelTimeSeriesQuery.isLoading, channelTimeSeriesQuery.isPreviousData)}
                    />}
                </Box>
            </Stack>
            )}

            {showSeasonality && (
            <Stack gap={1}>
                <Typography variant="body1" color="text.secondary">
                    {t("Other")}
                </Typography>

                {/* Twelve months across seven years need the whole row; this never shares one. */}
                <SeasonalityCard
                    data={seasonalityQuery.data}
                    loading={insightsLoadingOf(seasonalityQuery.isLoading)}
                />
            </Stack>
            )}
        </Stack>
    );
}

export default CrmOverviewTab;
